import { CdTDocument, DocumentTags } from '@app/features/documents/document.model';

/**
 * Extensions reconnues pour chaque type de fichier, la première étant celle qu'on ajoute
 * quand un nom n'en porte pas.
 */
const extensionsByMimeType: Record<string, string[]> = {
  'application/pdf': ['pdf'],
  'image/jpeg': ['jpeg', 'jpg'],
  'image/png': ['png'],
  'image/gif': ['gif'],
  'image/heic': ['heic'],
  'image/heif': ['heif'],
  'image/webp': ['webp'],
};

/** Ce que la visionneuse sait faire d'un document. */
export type DocumentKind = 'image' | 'pdf' | 'unsupported';

function normalize(mimeType: string | null | undefined): string {
  return (mimeType ?? '').split(';')[0].trim().toLowerCase();
}

/** Extension d'un type de fichier, sans point ; `null` pour un type inconnu. */
export function extensionForMimeType(mimeType: string | null | undefined): string | null {
  return extensionsByMimeType[normalize(mimeType)]?.[0] ?? null;
}

/**
 * Nom sous lequel la visionneuse propose de télécharger un document : son nom, à défaut le
 * libellé de sa nature, complété de l'extension que dit son type (RG-document-telechargement-
 * depuis-la-visionneuse). Un contrat se télécharge sous son nom de téléchargement, celui du
 * disque réseau (RG-contrat-nom-de-telechargement). Le type est celui du document, à défaut
 * celui du fichier reçu.
 */
export function downloadFileName(document: CdTDocument, blob?: Blob | null): string {
  const mimeType = normalize(document.mimeType) || normalize(blob?.type);
  const extensions = extensionsByMimeType[mimeType] ?? [];
  const base =
    document.downloadName?.trim() ||
    document.name?.trim() ||
    DocumentTags.labels[document.tag] ||
    document.tag ||
    'document';
  const lower = base.toLowerCase();
  if (extensions.length === 0 || extensions.some((ext) => lower.endsWith(`.${ext}`))) {
    return base;
  }
  return `${base}.${extensions[0]}`;
}

/**
 * Ce que la visionneuse peut afficher d'un fichier : une image, un PDF — y compris servi sans
 * type précis mais nommé en `.pdf` —, ou rien.
 *
 * Toute image est tentée, HEIC compris : c'est le navigateur qui dit s'il sait la lire, et la
 * visionneuse se replie sur le message de format non affichable quand il n'y parvient pas.
 */
export function documentKind(
  mimeType: string | null | undefined,
  name?: string | null,
): DocumentKind {
  const type = normalize(mimeType);
  if (type === 'application/pdf') {
    return 'pdf';
  }
  if (type.startsWith('image/')) {
    return 'image';
  }
  if ((type === '' || type === 'application/octet-stream') && /\.pdf$/i.test(name ?? '')) {
    return 'pdf';
  }
  return 'unsupported';
}

/**
 * Type sous lequel présenter le fichier d'un document au navigateur : celui du document, à défaut
 * celui du fichier reçu — et `application/pdf` pour tout ce que `documentKind()` reconnaît comme
 * PDF, y compris un `scan.pdf` servi sans type précis, que le navigateur téléchargerait sinon.
 */
export function displayMimeType(document: CdTDocument, blob?: Blob | null): string {
  const type = document.mimeType || blob?.type || '';
  return documentKind(type, document.name) === 'pdf' ? 'application/pdf' : type;
}
